import { defineStore } from 'pinia';
import DeviceService from '@/services/DeviceService';
import AssetShadowService from '@/services/AssetShadowService';
import { useProjectStore } from '@/stores/ProjectStore';
import { extractCoords } from '@/@core/utils/geo';
import { extractTelemetryFields } from '@/@core/utils/shadowState';
import { logger } from '@sentry/vue';

export const useDeviceStore = defineStore('DeviceStore', {
    state: () => ({
        devices: [],
        device: null,
        stats: null,
        loading: false,
        error: null,
        deviceMap: new Map(),
    }),
    
    getters: {
        getDevices: (state) => state.devices,
        totalDevices: (state) => state.devices.length,
        activeDevices: (state) => state.devices.filter(d => d.status === 'active'),
        offlineDevices: (state) => state.devices.filter(d => d.online === false),
        getDeviceById: (state) => (id) => state.deviceMap.get(id),
    },

    actions: {
        async fetchDevices(params = {}) {
            this.loading = true;
            this.error = null;
            try {
                const response = await DeviceService.getDevices(params);
                
                // Handle response structure (unwrapping if necessary)
                // Assuming response.data is the list, or response itself is the list
                const devicesList = Array.isArray(response) ? response : (response.data || []);
                
                this.devices = devicesList.items || devicesList; // Handle pagination wrapper if exists

                console.log('Fetched devices:', this.devices);
                
                // Rebuild map for faster lookups
                this.deviceMap.clear();
                this.devices.forEach(d => {
                    this.deviceMap.set(d._id, d);
                });

                return this.devices;
            } catch (error) {
                console.error('Error fetching devices:', error);
                this.error = error;
                // Don't swallow error, let caller handle if needed
                throw error;
            } finally {
                this.loading = false;
            }
        },

        async fetchDeviceStats() {
            try {
                this.stats = await DeviceService.getDeviceStats();
                return this.stats;
            } catch (error) {
                console.error('Error fetching device stats:', error);
                this.error = error;
            }
        },

        async getDevice(id) {
            this.loading = true;
             try {
                const device = await DeviceService.getDevice(id);
                this.device = device;
                return device;
            } catch (error) {
                this.error = error;
                throw error;
            } finally {
                this.loading = false;
            }
        },

        async createDevice(payload) {
            this.loading = true;
            try {
                const newDevice = await DeviceService.createDevice(payload, payload.project_id);
                
                // Add to local state
                this.devices.push(newDevice);
                this.deviceMap.set(newDevice._id, newDevice);
                
                return newDevice;
            } catch (error) {
                this.error = error;
                throw error;
            } finally {
                this.loading = false;
            }
        },

        async updateDevice(id, payload) {
            this.loading = true;
            try {
                const updatedDevice = await DeviceService.updateDevice(id, payload);
                
                // Update local state
                const index = this.devices.findIndex(d => d._id === id);
                if (index !== -1) {
                    this.devices[index] = { ...this.devices[index], ...updatedDevice };
                }
                this.deviceMap.set(id, { ...this.deviceMap.get(id), ...updatedDevice });
                
                return updatedDevice;
            } catch (error) {
                this.error = error;
                throw error;
            } finally {
                this.loading = false;
            }
        },

        async deleteDevice(id) {
            this.loading = true;
            try {
                await DeviceService.deleteDevice(id);
                
                // Remove from local state
                this.devices = this.devices.filter(d => d._id !== id);
                this.deviceMap.delete(id);
                
            } catch (error) {
                this.error = error;
                throw error;
            } finally {
                this.loading = false;
            }
        },

        /**
         * Fetch last-known positions from the Shadows API and merge into
         * existing device records.  Falls back silently if shadows are
         * unreachable so the map still loads from GET /devices data.
         */
        async fetchPositions() {
            try {
                // Build params — explicit project_id when a project is active
                const params = { limit: 5000 }
                try {
                    const projectStore = useProjectStore()
                    if (projectStore.activeProjectId) {
                        params.project_id = projectStore.activeProjectId
                    }
                } catch { /* ProjectStore may not be initialized yet */ }

                const response = await AssetShadowService.getShadows(params)
                const raw = response?.data ?? response
                const shadows = Array.isArray(raw)
                    ? raw
                    : Array.isArray(raw?.items) ? raw.items
                    : Array.isArray(raw?.shadows) ? raw.shadows
                    : []

                // Build reverse map: assetId → [deviceId, …] from device records
                const assetToDevices = new Map()
                for (const [devId, device] of this.deviceMap) {
                    const aid = device.asset_id
                    if (!aid) continue
                    if (!assetToDevices.has(aid)) assetToDevices.set(aid, [])
                    assetToDevices.get(aid).push(devId)
                }

                for (const shadow of shadows) {
                    const shadowId = shadow._id

                    // ── Strategy 1: Shadow has a `devices` map (keyed by role) ──
                    // Each entry carries its own device_id we can match directly.
                    const devicesMap = shadow.devices || {}
                    for (const [, entry] of Object.entries(devicesMap)) {
                        const devId = entry.device_id
                        if (!devId) continue
                        const device = this.deviceMap.get(devId)
                        if (!device) continue

                        const patch = {}
                        const entryCoords = extractCoords(entry)
                        const shadowCoords = extractCoords(shadow)
                        if (entryCoords) patch.geometry = { type: 'Point', coordinates: entryCoords }
                        else if (shadowCoords) patch.geometry = { type: 'Point', coordinates: shadowCoords }
                        if (entry.online != null) patch.online = entry.online
                        else if (shadow.online != null) patch.online = shadow.online

                        const p = entry.payload || {}
                        if (p['position.speed'] != null) patch.speed = p['position.speed']
                        if (p['position.direction'] != null) patch.heading = p['position.direction']
                        if (p['engine.ignition.status'] != null) patch.ignition = p['engine.ignition.status']
                        if (p['fuel.level.percent'] != null) patch.fuel = p['fuel.level.percent']
                        if (p['battery.voltage'] != null || p['external.powersource.voltage'] != null) {
                            patch.battery = p['battery.voltage'] ?? p['external.powersource.voltage']
                        }

                        const merged = { ...device, ...patch }
                        this.deviceMap.set(devId, merged)
                        const idx = this.devices.findIndex(d => d._id === devId)
                        if (idx !== -1) this.devices[idx] = merged
                    }

                    // ── Strategy 2: Reverse-lookup via device.asset_id ──────────
                    // For shadows without a `devices` map, use current_state + location
                    // and apply to all devices linked to this asset.
                    const linkedDevIds = assetToDevices.get(shadowId) || []
                    for (const devId of linkedDevIds) {
                        // Skip if already patched by Strategy 1
                        if (devicesMap && Object.values(devicesMap).some(e => e.device_id === devId)) continue

                        const device = this.deviceMap.get(devId)
                        if (!device) continue

                        const patch = {}
                        const sCoords = extractCoords(shadow)
                        if (sCoords) patch.geometry = { type: 'Point', coordinates: sCoords }
                        if (shadow.online != null) patch.online = shadow.online

                        // Extract telemetry from shadow channels (new) or current_state (legacy)
                        const tel = extractTelemetryFields(shadow)
                        if (tel.speed)    patch.speed    = tel.speed
                        if (tel.heading)  patch.heading  = tel.heading
                        if (tel.ignition != null) patch.ignition = tel.ignition
                        if (tel.fuel != null)     patch.fuel     = tel.fuel
                        if (tel.battery != null)  patch.battery  = tel.battery
                        if (tel.landmark) patch.landmark = tel.landmark

                        const merged = { ...device, ...patch }
                        this.deviceMap.set(devId, merged)
                        const idx = this.devices.findIndex(d => d._id === devId)
                        if (idx !== -1) this.devices[idx] = merged
                    }
                }

                console.log(`[DeviceStore] Merged ${shadows.length} shadow(s) → ${this.devices.length} device(s)`)

                // Return raw shadows so callers can seed TelemetryStore
                return shadows
            } catch (e) {
                // Graceful degradation — map still works with device-only data
                console.warn('[DeviceStore] Shadows API unreachable, using device locations:', e.message)
                return []
            }
        },

        // Helper to update device state from real-time events (SocketService)
        updateDeviceRealtime(id, partialData) {
            console.log('Real-time update for device:', id, partialData);
            if (this.deviceMap.has(id)) {
                const currentDevice = this.deviceMap.get(id);
                const updatedDevice = { ...currentDevice, ...partialData };
                
                this.deviceMap.set(id, updatedDevice);
                
                const index = this.devices.findIndex(d => d._id === id);
                if (index !== -1) {
                    this.devices[index] = updatedDevice;
                }

                logger.info('Updated device from real-time event:', updatedDevice);
            }
        }
    }
});